import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API } from "../App";
import { devWarn } from "../utils/devLogger";
import { Button } from "../components/ui/button";
import {
  Sparkles, TreePine, Ship, Star, Mountain, CloudRain,
  Play, Pause, SkipBack, Clock, Heart, Volume2, Loader2,
  ChevronDown, Lock, Crown, Download, Moon, Gem
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const THEME_ICONS = {
  enchanted_forest: TreePine,
  ocean_voyage: Ship,
  stargazing: Star,
  mountain_lodge: Mountain,
  garden_rain: CloudRain,
  auto: Sparkles,
};

const THEME_COLORS = {
  enchanted_forest: "from-emerald-400 to-teal-500",
  ocean_voyage: "from-blue-400 to-cyan-500",
  stargazing: "from-violet-400 to-indigo-500",
  mountain_lodge: "from-amber-400 to-orange-500",
  garden_rain: "from-sky-400 to-slate-400",
  auto: "from-indigo-400 to-violet-500",
};

const formatTime = (s) => {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

// Story Player
const StoryPlayer = ({ story, onBack, onFavorite }) => {
  const { t } = useTranslation();
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFav, setIsFav] = useState(story.is_favorite);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => setCurrentTime(audio.currentTime);
    const onDur = () => { if (audio.duration && isFinite(audio.duration)) setDuration(audio.duration); };
    const onEnd = () => { setIsPlaying(false); axios.post(`${API}/sleep-stories/${story.id}/play`).catch(() => {}); };
    const onError = () => toast.error(t('mindfulness.sleepStories.audioFailed'));
    const onCanPlay = () => { if (audio.duration && isFinite(audio.duration) && duration === 0) setDuration(audio.duration); };

    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onDur);
    audio.addEventListener("durationchange", onDur);
    audio.addEventListener("canplay", onCanPlay);
    audio.addEventListener("ended", onEnd);
    audio.addEventListener("error", onError);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onDur);
      audio.removeEventListener("durationchange", onDur);
      audio.removeEventListener("canplay", onCanPlay);
      audio.removeEventListener("ended", onEnd);
      audio.removeEventListener("error", onError);
    };
  }, [story.id, duration]);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) audioRef.current.pause();
    else audioRef.current.play().catch(() => {});
    setIsPlaying(!isPlaying);
  };

  const seek = (e) => {
    if (!audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    audioRef.current.currentTime = pct * duration;
  };

  const handleFav = async () => {
    try {
      const res = await axios.post(`${API}/sleep-stories/${story.id}/favorite`);
      setIsFav(res.data.is_favorite);
      onFavorite?.(story.id, res.data.is_favorite);
    } catch (err) { devWarn("Favorite toggle failed:", err.message); }
  };

  const handleDownload = async () => {
    try {
      const audioUrl = `${API.replace("/api", "")}${story.audio_url}`;
      const res = await fetch(audioUrl);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${story.title.replace(/[^a-zA-Z0-9 ]/g, "").replace(/\s+/g, "_")}.mp3`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(t('mindfulness.sleepStories.downloaded'));
    } catch (err) {
      devWarn("Download failed:", err.message);
      toast.error(t('mindfulness.sleepStories.downloadFailed'));
    }
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const themeGrad = THEME_COLORS[story.theme] || THEME_COLORS.auto;
  const ThemeIcon = THEME_ICONS[story.theme] || Sparkles;

  return (
    <div className="text-center" data-testid="story-player">
      <audio ref={audioRef} src={`${API.replace("/api", "")}${story.audio_url}`} preload="metadata" />

      <div className="relative w-48 h-48 mx-auto mb-8">
        <div className={`absolute inset-0 rounded-full bg-gradient-to-br ${themeGrad} opacity-20 ${isPlaying ? "animate-pulse" : ""}`}
          style={{ animationDuration: "5s" }} />
        <div className={`absolute inset-4 rounded-full bg-gradient-to-br ${themeGrad} opacity-10 ${isPlaying ? "animate-pulse" : ""}`}
          style={{ animationDuration: "4s", animationDelay: "0.5s" }} />
        <div className="absolute inset-8 rounded-full bg-white/80 dark:bg-slate-800/80 backdrop-blur flex items-center justify-center">
          <ThemeIcon className={`w-12 h-12 ${isPlaying ? "text-indigo-500 animate-pulse" : "text-indigo-400"}`} strokeWidth={1.5} />
        </div>
      </div>

      <h2 className="text-2xl font-light text-indigo-900 dark:text-indigo-100 mb-1" style={{ fontFamily: "Outfit" }}>
        {story.title}
      </h2>
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
        {story.theme_label} · {story.duration_minutes} min · {story.voice}
      </p>

      <div className="max-w-md mx-auto mb-2 cursor-pointer" onClick={seek} data-testid="story-progress-bar">
        <div className="h-1.5 bg-indigo-100 dark:bg-slate-700 rounded-full overflow-hidden">
          <div className={`h-full rounded-full bg-gradient-to-r ${themeGrad} transition-all`} style={{ width: `${progress}%` }} />
        </div>
      </div>
      <div className="flex justify-between max-w-md mx-auto text-xs text-slate-400 mb-6">
        <span>{formatTime(currentTime)}</span>
        <span>{formatTime(duration)}</span>
      </div>

      <div className="flex items-center justify-center gap-4 mb-6">
        <div className="flex flex-col items-center gap-0.5">
          <Button variant="ghost" size="icon" onClick={handleFav} data-testid="story-favorite-btn"
            className={isFav ? "text-amber-500" : "text-slate-400"}>
            <Heart className={`w-5 h-5 ${isFav ? "fill-amber-400" : ""}`} />
          </Button>
          <span className="text-[9px] font-medium tracking-wider text-slate-400 uppercase">{isFav ? t('mindfulness.sleepStories.saved') : t('mindfulness.sleepStories.save')}</span>
        </div>
        <Button onClick={togglePlay}
          className={`w-16 h-16 rounded-full bg-gradient-to-r ${themeGrad} text-white hover:opacity-90 shadow-lg`}
          data-testid="story-play-pause-btn">
          {isPlaying ? <Pause className="w-7 h-7" /> : <Play className="w-7 h-7 ml-1" />}
        </Button>
        <Button variant="ghost" size="icon" onClick={onBack} className="text-slate-400" data-testid="story-back-btn">
          <SkipBack className="w-5 h-5" />
        </Button>
        <div className="flex flex-col items-center gap-0.5">
          <Button variant="ghost" size="icon" onClick={handleDownload} data-testid="story-download-btn"
            className="text-slate-400 hover:text-indigo-500">
            <Download className="w-5 h-5" />
          </Button>
          <span className="text-[9px] font-medium tracking-wider text-slate-400 uppercase">{t('mindfulness.sleepStories.save')}</span>
        </div>
      </div>
    </div>
  );
};

// Generation Loader
const StoryGenerationLoader = ({ progress, t }) => {
  const getStageText = (p) => {
    if (p < 10) return t('mindfulness.sleepStories.stageCrafting');
    if (p < 25) return t('mindfulness.sleepStories.stageWriting');
    if (p < 90) return t('mindfulness.sleepStories.stageNarrating');
    if (p < 100) return t('mindfulness.sleepStories.stagePreparing');
    return t('mindfulness.sleepStories.stageAlmost');
  };

  return (
    <div className="text-center py-12" data-testid="story-generation-loader">
      <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-indigo-400/20 to-violet-400/20 flex items-center justify-center">
        <Moon className="w-10 h-10 text-indigo-500 animate-pulse" strokeWidth={1.5} />
      </div>
      <h3 className="text-xl font-light text-indigo-900 dark:text-indigo-100 mb-2" style={{ fontFamily: "Outfit" }}>
        {t('mindfulness.sleepStories.creatingStory')}
      </h3>
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">{getStageText(progress)}</p>
      <div className="max-w-xs mx-auto">
        <div className="h-1.5 bg-indigo-100 dark:bg-slate-700 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-indigo-400 to-violet-400 rounded-full transition-all duration-700 ease-out"
            style={{ width: `${Math.max(progress, 3)}%` }} />
        </div>
        <p className="text-xs text-slate-400 mt-2 tabular-nums">{progress}%</p>
      </div>
      <p className="text-xs text-slate-400 mt-4">
        {progress < 25 ? t('mindfulness.sleepStories.weavingNarrative') : t('mindfulness.sleepStories.convertingAudio')}
      </p>
    </div>
  );
};

// Main Sleep Stories Component
const SleepStories = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [themes, setThemes] = useState([]);
  const [durations, setDurations] = useState([15]);
  const [voices, setVoices] = useState([]);
  const [library, setLibrary] = useState([]);
  const [selectedTheme, setSelectedTheme] = useState("auto");
  const [selectedDuration, setSelectedDuration] = useState(15);
  const [selectedVoice, setSelectedVoice] = useState("nova");
  const [generating, setGenerating] = useState(false);
  const [genProgress, setGenProgress] = useState(0);
  const [activeStory, setActiveStory] = useState(null);
  const [showLibrary, setShowLibrary] = useState(false);
  const [quota, setQuota] = useState(null);

  const fetchQuota = async () => {
    try {
      const res = await axios.get(`${API}/sleep-stories/quota`);
      setQuota(res.data);
    } catch (err) { devWarn("Story quota fetch:", err.message); }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [themesRes, libraryRes] = await Promise.all([
          axios.get(`${API}/sleep-stories/themes`),
          axios.get(`${API}/sleep-stories/library`),
        ]);
        setThemes(themesRes.data.themes || []);
        setDurations(themesRes.data.durations || [15]);
        setVoices(themesRes.data.voices || []);
        setLibrary(libraryRes.data.stories || []);
      } catch (err) { devWarn("Sleep stories load failed:", err.message); }
    };
    load();
    fetchQuota();
  }, []);

  const handleGenerate = async () => {
    setGenerating(true);
    setGenProgress(0);
    try {
      const startRes = await axios.post(`${API}/sleep-stories/generate`, {
        theme: selectedTheme,
        duration_minutes: selectedDuration,
        voice: selectedVoice,
        language: i18n.language || "en",
      });

      if (startRes.data.cached && startRes.data.story) {
        setActiveStory(startRes.data.story);
        setLibrary(prev => [startRes.data.story, ...prev]);
        toast.success(t('mindfulness.sleepStories.storyReadyCached'));
        fetchQuota();
        setGenerating(false);
        return;
      }

      const taskId = startRes.data.task_id;
      if (!taskId) throw new Error("No task_id returned");

      const maxAttempts = 240;
      for (let i = 0; i < maxAttempts; i++) {
        await new Promise(r => setTimeout(r, 4000));
        try {
          const statusRes = await axios.get(`${API}/sleep-stories/task/${taskId}`);
          const { status, progress, story, error } = statusRes.data;
          setGenProgress(progress || 0);
          if (status === "complete" && story) {
            setActiveStory(story);
            setLibrary(prev => [story, ...prev]);
            toast.success(t('mindfulness.sleepStories.storyReady'));
            fetchQuota();
            return;
          }
          if (status === "failed") {
            toast.error(error || "Generation failed. Please try again.");
            return;
          }
        } catch (pollErr) {
          devWarn("Poll error:", pollErr.message);
        }
      }
      toast.error("Generation timed out. Please try again.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start story generation");
    } finally {
      setGenerating(false);
      setGenProgress(0);
    }
  };

  const handleFavorite = (id, isFav) => {
    setLibrary(prev => prev.map(s => s.id === id ? { ...s, is_favorite: isFav } : s));
  };

  if (generating) return <StoryGenerationLoader progress={genProgress} t={t} />;

  if (activeStory) {
    return <StoryPlayer story={activeStory} onBack={() => setActiveStory(null)} onFavorite={handleFavorite} />;
  }

  const favorites = library.filter(s => s.is_favorite);

  // Premium lock — free users see Diamond paywall
  if (quota && quota.locked) {
    return (
      <div className="text-center py-8" data-testid="stories-paywall">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-violet-100 to-indigo-100 flex items-center justify-center">
          <Gem className="w-8 h-8 text-violet-500" />
        </div>
        <h3 className="text-lg font-medium text-indigo-900 dark:text-indigo-100 mb-2" style={{ fontFamily: "Outfit" }}>
          {t('subscription.sleepStoriesDiamond')}
        </h3>
        <p className="text-sm text-slate-500 max-w-sm mx-auto mb-4">
          {t('mindfulness.sleepStories.premiumDesc')}
        </p>
        <Button onClick={() => navigate("/subscription")}
          className="bg-gradient-to-r from-violet-500 to-indigo-500 hover:from-violet-600 hover:to-indigo-600 text-white"
          data-testid="stories-upgrade-btn">
          <Gem className="w-4 h-4 mr-1.5" /> {t('subscription.upgradeDiamond')}
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="sleep-stories-section">
      {/* Theme Selector */}
      <div className="mb-6">
        <h3 className="text-base font-medium text-indigo-800 dark:text-indigo-200 mb-3" style={{ fontFamily: "Outfit" }}>
          {t('mindfulness.sleepStories.chooseWorld')}
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {themes.map(th => {
            const Icon = THEME_ICONS[th.id] || Sparkles;
            const isSelected = selectedTheme === th.id;
            const label = t(`mindfulness.sleepStories.themes.${th.id}.label`, th.label);
            const desc = t(`mindfulness.sleepStories.themes.${th.id}.description`, th.description);
            return (
              <button key={th.id} onClick={() => setSelectedTheme(th.id)}
                className={`p-3 rounded-xl text-left transition-all ${
                  isSelected
                    ? "bg-indigo-100 dark:bg-indigo-900/40 border-2 border-indigo-400 shadow-sm"
                    : "bg-white/50 dark:bg-slate-800/50 border-2 border-transparent hover:border-indigo-200"
                }`} data-testid={`story-theme-${th.id}`}>
                <Icon className={`w-5 h-5 mb-1 ${isSelected ? "text-indigo-500" : "text-slate-400"}`} strokeWidth={1.5} />
                <p className={`text-sm font-medium ${isSelected ? "text-indigo-700 dark:text-indigo-300" : "text-slate-600 dark:text-slate-400"}`}>
                  {label}
                </p>
                <p className="text-xs text-slate-400 dark:text-slate-500">{desc}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Duration + Voice */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div>
          <h3 className="text-sm font-medium text-indigo-800 dark:text-indigo-200 mb-2">{t('mindfulness.sleepStories.duration')}</h3>
          <div className="flex gap-2">
            {durations.map(d => (
              <button key={d} onClick={() => setSelectedDuration(d)}
                className={`flex-1 py-2 rounded-lg text-sm transition-all ${
                  selectedDuration === d
                    ? "bg-indigo-400 text-white shadow"
                    : "bg-white/60 dark:bg-slate-800/60 text-slate-600 dark:text-slate-400 hover:bg-indigo-50"
                }`} data-testid={`story-duration-${d}`}>~{d}m</button>
            ))}
          </div>
        </div>
        <div>
          <h3 className="text-sm font-medium text-indigo-800 dark:text-indigo-200 mb-2">{t('mindfulness.sleepStories.voice')}</h3>
          <div className="flex gap-2 flex-wrap">
            {voices.map(v => (
              <button key={v.id} onClick={() => setSelectedVoice(v.id)}
                className={`px-3 py-2 rounded-lg text-sm transition-all ${
                  selectedVoice === v.id
                    ? "bg-indigo-400 text-white shadow"
                    : "bg-white/60 dark:bg-slate-800/60 text-slate-600 dark:text-slate-400 hover:bg-indigo-50"
                }`} data-testid={`story-voice-${v.id}`}>{t(`mindfulness.sleepStories.voices.${v.id}.label`, v.label)}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Quota + Generate */}
      {quota && quota.remaining <= 0 ? (
        <div className="mb-8 p-3 rounded-xl bg-indigo-50 border border-indigo-200 text-center" data-testid="story-daily-limit">
          <p className="text-sm text-indigo-700">
            {quota.period === "48h"
              ? t('subscription.proGrandfathered48h')
              : t('mindfulness.sleepStories.dailyLimitReached', { limit: quota.limit })}
          </p>
        </div>
      ) : (
        <>
          {quota && quota.tier !== "founder" && (
            <div className="flex items-center justify-between mb-2 px-1" data-testid="story-quota-info">
              <span className="text-xs text-slate-500">
                {quota.period === "48h"
                  ? `${quota.remaining}/${quota.limit} remaining (resets in 48h)`
                  : t('mindfulness.sleepStories.storiesRemaining', { remaining: quota.remaining, limit: quota.limit })}
              </span>
              {quota.tier === "pro" && (
                <span className="text-xs text-violet-500 cursor-pointer hover:underline" onClick={() => navigate("/subscription")}>
                  Upgrade to Diamond
                </span>
              )}
            </div>
          )}
          <Button onClick={handleGenerate}
            className="w-full py-6 text-base mb-8 bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-600 hover:to-violet-600 text-white shadow-lg"
            disabled={quota && quota.remaining <= 0} data-testid="generate-story-btn">
            <Moon className="w-5 h-5 mr-2" /> {t('mindfulness.sleepStories.generateBtn')}
          </Button>
        </>
      )}

      {/* Favorites */}
      {favorites.length > 0 && (
        <div className="mb-6">
          <h3 className="text-base font-medium text-indigo-800 dark:text-indigo-200 mb-3 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <Heart className="w-4 h-4 text-amber-400 fill-amber-400" /> {t('mindfulness.sleepStories.favorites')}
          </h3>
          <div className="space-y-2">
            {favorites.map(s => {
              const Icon = THEME_ICONS[s.theme] || Sparkles;
              return (
                <button key={s.id} onClick={() => setActiveStory(s)}
                  className="w-full flex items-center gap-3 p-3 rounded-xl bg-white/50 dark:bg-slate-800/50 hover:bg-indigo-50 dark:hover:bg-slate-700 transition-all text-left"
                  data-testid={`story-fav-${s.id}`}>
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${THEME_COLORS[s.theme] || THEME_COLORS.auto} flex items-center justify-center opacity-80`}>
                    <Icon className="w-5 h-5 text-white" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-indigo-800 dark:text-indigo-200 truncate">{s.title}</p>
                    <p className="text-xs text-slate-400">{s.duration_minutes}m · {s.voice}</p>
                  </div>
                  <Play className="w-4 h-4 text-indigo-400" />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Library */}
      {library.length > 0 && (
        <div>
          <button onClick={() => setShowLibrary(!showLibrary)}
            className="w-full flex items-center justify-between text-sm font-medium text-indigo-800 dark:text-indigo-200 mb-3"
            data-testid="toggle-story-library">
            <span style={{ fontFamily: "Outfit" }}>{t('mindfulness.sleepStories.yourStories')} ({library.length})</span>
            <ChevronDown className={`w-4 h-4 transition-transform ${showLibrary ? "rotate-180" : ""}`} />
          </button>
          {showLibrary && (
            <div className="space-y-2">
              {library.map(s => {
                const Icon = THEME_ICONS[s.theme] || Sparkles;
                return (
                  <button key={s.id} onClick={() => setActiveStory(s)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl bg-white/50 dark:bg-slate-800/50 hover:bg-indigo-50 dark:hover:bg-slate-700 transition-all text-left"
                    data-testid={`story-lib-${s.id}`}>
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${THEME_COLORS[s.theme] || THEME_COLORS.auto} flex items-center justify-center opacity-80`}>
                      <Icon className="w-5 h-5 text-white" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-indigo-800 dark:text-indigo-200 truncate">{s.title}</p>
                      <p className="text-xs text-slate-400">{s.theme_label} · {s.duration_minutes}m · {new Date(s.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {s.is_favorite && <Heart className="w-3 h-3 text-amber-400 fill-amber-400" />}
                      <Play className="w-4 h-4 text-indigo-400" />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SleepStories;
